import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, formatApiError } from "../lib/api";
import { useT } from "../lib/i18n";
import { DownloadSimple, PenNib, CheckCircle, FileText, Package, Info, ArrowRight } from "@phosphor-icons/react";
import { downloadJsonFile } from "../lib/downloads";
import { getVersionReadiness } from "../lib/versionReadiness";

const CATEGORIES = [
  { key: "regulatory", labelKey: "docs.cat_regulatory", types: ["cover", "classification", "technical_file", "clinical", "doc"] },
  { key: "quality", labelKey: "docs.cat_quality", types: ["risk"] },
  { key: "compliance", labelKey: "docs.cat_compliance", types: ["ai_act", "gaps"] },
];

const JSON_SOURCE_FIELDS = {
  cover: ["intake", "classification", "risk", "clinical", "ai_act", "compliance_gaps", "llm"],
  classification: ["classification", "intake", "llm"],
  technical_file: ["intake", "classification", "risk", "clinical", "gspr_state"],
  clinical: ["clinical", "intake"],
  risk: ["risk"],
  ai_act: ["ai_act"],
  gaps: ["compliance_gaps"],
};

const SIGNABLE_DOC_TYPES = new Set(Object.keys(JSON_SOURCE_FIELDS));

function categoriesForDocuments(docs) {
  const knownTypes = new Set(CATEGORIES.flatMap((category) => category.types));
  const otherTypes = [...new Set(
    docs.map((doc) => doc.type).filter((type) => type && !knownTypes.has(type)),
  )];
  return otherTypes.length
    ? [...CATEGORIES, { key: "other", labelKey: "docs.cat_other", types: otherTypes }]
    : CATEGORIES;
}

export default function DocumentsSection({ project, version, deviceId, roleView = "manufacturer", lang, onChange, showPackageAction = true, canManageDocuments = true }) {
  const t = useT();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [signingAll, setSigningAll] = useState(false);
  const [hasSig, setHasSig] = useState(false);
  const [freezing, setFreezing] = useState(false);
  const [actionError, setActionError] = useState("");

  function reportActionError(error) {
    const detail = error.response?.data?.detail;
    setActionError(detail?.code === "version_review_required"
      ? t("sub.not_ready_sub")
      : formatApiError(detail));
  }

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [{ data: docList }, sigRes] = await Promise.all([
        api.get(`/projects/${project.id}/versions/${version.version}/documents`, {
          params: deviceId ? { device_id: deviceId } : {},
        }),
        api.get(`/user/signature`).catch(() => ({ data: { has_signature: false } })),
      ]);
      setDocs((docList || []).filter(
        (doc) => doc.type !== "ai_act" || version.ai_act?.applicable === true,
      ));
      setHasSig(!!sigRes.data?.has_signature);
    } catch {
      setDocs([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [project.id, version.version, version.ai_act?.applicable, deviceId]);

  useEffect(() => { load(); }, [load]);

  async function fetchBlob(path, filename, key) {
    setBusy(key);
    try {
      const res = await fetch(`${API}${path}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("rg_token")}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } finally { setBusy(null); }
  }

  function downloadOne(doc) {
    fetchBlob(
      `/projects/${project.id}/versions/${version.version}/documents/${doc.type}/pdf?lang=${lang}${deviceId ? `&device_id=${encodeURIComponent(deviceId)}` : ""}`,
      `${doc.code}_${project.id.slice(0, 8)}_v${version.version}.pdf`,
      doc.type
    );
  }

  function downloadDocumentJson(doc) {
    const sourceData = Object.fromEntries(
      (JSON_SOURCE_FIELDS[doc.type] || [])
        .filter((key) => version[key] !== undefined)
        .map((key) => [key, version[key]])
    );
    downloadJsonFile({
      schema: "airegulatory.document-source.v1",
      document_type: doc.type,
      document: {
        code: doc.code,
        title: lang === "es" ? doc.title_es : doc.title_en,
        version: version.version,
      },
      project: { id: project.id, name: project.name, device_name: project.device_name },
      source_data: sourceData,
      limitations: [
        "JSON de datos de origen; no sustituye el PDF revisado ni constituye un envío a EUDAMED.",
        "Revise la aplicabilidad y exactitud de los datos antes de utilizarlos.",
      ],
    }, `${doc.code}_${project.id.slice(0, 8)}_v${version.version}.json`);
  }

  async function downloadAll() {
    setDownloading(true);
    try {
      const params = new URLSearchParams({ lang, role_view: roleView });
      if (deviceId) params.set("device_id", deviceId);
      const res = await fetch(`${API}/projects/${project.id}/versions/${version.version}/documents/pre-registration-package.zip?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("rg_token")}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mdrflow-documentacion-${project.id.slice(0, 8)}-v${version.version}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } finally { setDownloading(false); }
  }

  async function signOne(doc) {
    if (!getVersionReadiness(version).ready) return;
    if (!SIGNABLE_DOC_TYPES.has(doc.type)) return;
    if (!window.confirm(t("docs.confirm_sign"))) return;
    setActionError("");
    setBusy(doc.type);
    try {
      await api.post(
        `/projects/${project.id}/versions/${version.version}/documents/${doc.type}/sign`,
        {},
        { params: deviceId ? { device_id: deviceId } : {} },
      );
      await load();
      onChange?.();
    } catch (error) {
      reportActionError(error);
    } finally { setBusy(null); }
  }

  async function signAll() {
    if (!getVersionReadiness(version).ready) return;
    if (!window.confirm(t("docs.confirm_sign_all"))) return;
    setActionError("");
    setSigningAll(true);
    try {
      for (const d of docs.filter((doc) => SIGNABLE_DOC_TYPES.has(doc.type))) {
        if (!d.signature) {
          await api.post(
            `/projects/${project.id}/versions/${version.version}/documents/${d.type}/sign`,
            {},
            { params: deviceId ? { device_id: deviceId } : {} },
          );
        }
      }
      await load();
      onChange?.();
    } catch (error) {
      reportActionError(error);
      await load();
      onChange?.();
    } finally { setSigningAll(false); }
  }

  async function freezeVersion() {
    if (!getVersionReadiness(version).ready) return;
    if (!window.confirm(t("sub.confirm_freeze"))) return;
    setActionError("");
    setFreezing(true);
    try {
      await api.post(
        `/projects/${project.id}/versions/${version.version}/freeze`,
        {},
        { params: deviceId ? { device_id: deviceId } : {} },
      );
      onChange?.();
    } catch (error) {
      reportActionError(error);
    } finally {
      setFreezing(false);
    }
  }

  const signableDocs = docs.filter((doc) => SIGNABLE_DOC_TYPES.has(doc.type));
  const readiness = getVersionReadiness(version);
  const reviewAssessmentUrl = `/projects/new?project_id=${encodeURIComponent(project.id)}&device_id=${encodeURIComponent(deviceId || "")}`;

  return (
    <div className="grid-card p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border)] pb-4" data-testid="document-version-review">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-2)]">{t("sub.readiness")}</div>
          {version.frozen_at ? (
            <div className="mt-1 text-sm font-semibold text-emerald-700">
              {t("pd.frozen_banner")} · {version.frozen_at}
            </div>
          ) : (
            <div className="mt-1 text-sm text-[var(--text-2)]">{t("docs.draft_notice")}</div>
          )}
        </div>
        {canManageDocuments && !version.frozen_at && (
          <button type="button" onClick={freezeVersion} disabled={freezing || !readiness.ready}
            className="rounded-sm bg-emerald-700 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-800 disabled:opacity-50"
            data-testid="freeze-btn">
            {freezing ? t("sub.freezing") : t("sub.freeze")}
          </button>
        )}
      </div>
      {actionError ? <div role="alert" className="mb-4 rounded-sm border border-rose-500/40 bg-rose-500/10 p-3 text-sm" data-testid="document-action-error">{actionError}</div> : null}
      {!version.frozen_at && !readiness.ready ? (
        <div className="mb-4 flex flex-col gap-2 rounded-sm border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-[var(--text)] sm:flex-row sm:items-center sm:justify-between" role="status" data-testid="documents-review-required">
          <span>{t("docs.review_blocked")}</span>
          <div className="flex shrink-0 flex-wrap gap-3">
            {deviceId && readiness.classificationNeedsReview ? (
              <Link to={reviewAssessmentUrl} className="font-semibold text-[var(--accent-text)] underline underline-offset-2" data-testid="documents-review-assessment">
                {t("classification.review_answers")}
              </Link>
            ) : null}
            {readiness.highSeverityGaps.length > 0 ? (
              <Link to={`/projects/${project.id}?section=traceability&module=capa${deviceId ? `&device_id=${encodeURIComponent(deviceId)}` : ""}`} className="font-semibold text-[var(--accent-text)] underline underline-offset-2" data-testid="documents-review-gaps">
                {t("docs.open_gaps")}
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-2)]">{t("docs.title")}</div>
          <h3 className="text-lg font-bold mt-1">{t("sub.pack_title")}</h3>
          <p className="text-xs text-[var(--text-2)] mt-1">{t("docs.sub")}</p>
          <Link
            to={`/projects/${project.id}?section=traceability`}
            data-testid="docs-open-postmarket"
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[var(--accent-text)] hover:underline"
          >
            {t("docs.postmarket_link")} <ArrowRight size={13} weight="bold" />
          </Link>
          {!hasSig && (
            <div className="mt-3 inline-flex items-start gap-2 rounded-sm border border-[var(--border-bright)] bg-[var(--surface-elevated)] px-3 py-2 text-xs text-[var(--risk-warning)]" data-testid="sig-missing-banner">
              <Info size={14} weight="fill" className="mt-0.5 shrink-0" />
              <span>{t("sig.banner_missing")}</span>
            </div>
          )}
          {hasSig && (
            <div className="mt-3 inline-flex items-center gap-1.5 font-mono text-xs text-[var(--risk-success)]" data-testid="sig-ready-badge">
              <CheckCircle size={12} weight="fill" /> {t("sig.uploaded_ok")}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canManageDocuments && <button onClick={signAll} disabled={signingAll || !readiness.ready || signableDocs.length === 0 || signableDocs.every(d => d.signature)} data-testid="sign-all-btn"
            className="inline-flex items-center gap-1.5 rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-xs font-semibold text-[var(--text)] transition-colors hover:bg-[var(--surface-hover)] disabled:opacity-40">
            <PenNib size={14} weight="bold" /> {signingAll ? t("docs.signing") : t("docs.sign_all")}
          </button>}
          {showPackageAction && (
            <button onClick={downloadAll} disabled={downloading} data-testid="download-all-btn" className="btn-primary inline-flex items-center gap-1.5 px-3 py-2 text-xs">
              <Package size={14} weight="bold" /> {downloading ? "…" : t("docs.download_all")}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="py-6 text-center text-sm text-[var(--text-2)]" role="status" data-testid="documents-loading">{t("docs.loading")}</p>
      ) : null}
      {loadError ? (
        <div className="my-4 flex flex-col gap-3 rounded-sm border border-rose-500/30 bg-rose-500/5 p-4 text-sm text-[var(--text)] sm:flex-row sm:items-center sm:justify-between" role="alert" data-testid="documents-load-error">
          <span>{t("docs.load_error")}</span>
          <button type="button" onClick={load} className="rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-xs font-semibold hover:border-[var(--accent)]" data-testid="documents-retry">{t("app.retry")}</button>
        </div>
      ) : null}
      {!loading && !loadError && docs.length === 0 ? (
        <div className="my-4 rounded-sm border border-dashed border-[var(--border)] p-6 text-center text-sm text-[var(--text-2)]" data-testid="documents-empty">
          {t("docs.empty")}
        </div>
      ) : null}
      {!loading && !loadError && categoriesForDocuments(docs).map((cat) => {
        const catDocs = cat.types
          .map((ty) => docs.find((d) => d.type === ty))
          .filter(Boolean);
        if (catDocs.length === 0) return null;
        return (
          <div key={cat.key} className="mb-5" data-testid={`docs-cat-${cat.key}`}>
            <div className="mb-2 text-xs font-mono font-bold uppercase tracking-widest text-[var(--accent-text)]">{t(cat.labelKey)}</div>
            <div className="overflow-hidden rounded-sm border border-[var(--border)]">
              <table className="w-full text-sm">
                <thead className="border-b border-[var(--border)] bg-[var(--surface-elevated)]">
                  <tr className="text-left font-mono text-xs uppercase tracking-wider text-[var(--text-2)]">
                    <th className="px-4 py-2 w-24">Code</th>
                    <th className="px-4 py-2">{t("docs.title")}</th>
                    <th className="px-4 py-2 w-32">{t("docs.signed")}</th>
                    <th className="px-4 py-2 w-52 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {catDocs.map((d, i) => (
                    <tr key={d.type} className={i % 2 === 0 ? "bg-[var(--surface)]" : "bg-[var(--surface-elevated)]"} data-testid={`doc-row-${d.type}`}>
                      <td className="px-4 py-3 font-mono text-xs text-[var(--text-2)]">{d.code}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText size={14} className="text-[var(--text-2)]" />
                          <span>{lang === "es" ? d.title_es : d.title_en}</span>
                        </div>
                        {d.type === "doc" && (
                          <div className="mt-1 pl-5 text-xs text-[var(--risk-warning)]">
                            {t("docs.review_required")}
                          </div>
                        )}
                        {d.signature && (
                          <div className="mt-1 pl-5 text-[10px] font-mono text-[var(--risk-success)]">
                            {t("docs.signed_by")} {d.signature.signed_by_email} · {d.signature.signed_at?.slice(0, 19).replace("T", " ")} · SHA-256 {d.signature.signature_hash?.slice(0, 16)}…
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {d.signature ? (
                          <span className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-[var(--risk-success)]"><CheckCircle size={12} weight="fill" /> {t("docs.signed")}</span>
                        ) : (
                          <span className="font-mono text-xs text-[var(--risk-warning)]">{t("docs.unsigned")}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {canManageDocuments && readiness.ready && !d.signature && SIGNABLE_DOC_TYPES.has(d.type) && (
                            <button onClick={() => signOne(d)} disabled={busy === d.type} data-testid={`doc-sign-${d.type}`}
                              className="inline-flex items-center gap-1 rounded-sm border border-[var(--border-bright)] bg-[var(--surface)] px-2.5 py-1.5 text-xs font-semibold text-[var(--risk-success)] transition-colors hover:bg-[var(--surface-hover)] disabled:opacity-40">
                              <PenNib size={12} weight="bold" /> {t("docs.sign")}
                            </button>
                          )}
                          <button onClick={() => downloadOne(d)} disabled={busy === d.type} data-testid={`doc-download-${d.type}`}
                            className="inline-flex items-center gap-1 rounded-sm border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1.5 text-xs font-semibold text-[var(--text)] transition-colors hover:bg-[var(--surface-hover)] disabled:opacity-40">
                            <DownloadSimple size={12} weight="bold" /> {t("docs.download")}
                          </button>
                          {JSON_SOURCE_FIELDS[d.type] && (
                            <button onClick={() => downloadDocumentJson(d)} title={t("exports.download_json")} data-testid={`doc-json-${d.type}`}
                              className="rounded-sm border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1.5 font-mono text-[10px] font-bold text-[var(--text)] hover:bg-[var(--surface-hover)]">
                              JSON
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
